import { ReactNode } from "react";
import {
  AppWindow,
  Route,
  Layout,
  Workflow,
  Boxes,
  Database,
  Plug,
  Sparkles,
  FunctionSquare,
  ShieldCheck,
  Rocket,
  Braces,
} from "lucide-react";

/* One file, no external content deps: the building-block stack (left) and the
 * abstracted "what you can build" powers (right), shown side by side. */

interface BlockNode {
  id: string;
  level: number;
  name: string;
  madeOf: string;
  line: string;
  icon: ReactNode;
}

interface PowerCard {
  id: string;
  title: string;
  line: string;
  icon: ReactNode;
}

// Top → bottom = simplest → biggest: each piece combines into the one below it,
// up to the whole app.
const BLOCKS: BlockNode[] = [
  {
    id: "source",
    level: 1,
    name: "Source & Capability",
    madeOf: "your connected systems",
    line: "A database or API, plus the one-thing-each operations everything is built on.",
    icon: <Database size={17} />,
  },
  {
    id: "component",
    level: 2,
    name: "Component",
    madeOf: "made of properties",
    line: "A reusable piece of a screen — a field, a table, a button, a card.",
    icon: <Boxes size={17} />,
  },
  {
    id: "workflow",
    level: 3,
    name: "Workflow",
    madeOf: "made of steps",
    line: "The logic behind a screen — ordered steps that shape data and run actions.",
    icon: <Workflow size={17} />,
  },
  {
    id: "surface",
    level: 4,
    name: "Surface",
    madeOf: "made of components",
    line: "A single screen: what the user sees, reads, and acts on.",
    icon: <Layout size={17} />,
  },
  {
    id: "journey",
    level: 5,
    name: "Journey",
    madeOf: "made of surfaces",
    line: "One goal, start to finish — sign in, place an order, review a request.",
    icon: <Route size={17} />,
  },
  {
    id: "app",
    level: 6,
    name: "App",
    madeOf: "made of journeys",
    line: "The thing people open. It holds the session and decides where they land.",
    icon: <AppWindow size={17} />,
  },
];

const POWERS: PowerCard[] = [
  {
    id: "connect",
    title: "Turn any system into an app",
    line: "Connect a database or API and its operations become building blocks.",
    icon: <Plug size={16} />,
  },
  {
    id: "ai",
    title: "AI builds the screens",
    line: "Describe what you want — it generates the layout for you.",
    icon: <Sparkles size={16} />,
  },
  {
    id: "logic",
    title: "Logic without surprises",
    line: "One closed, deterministic expression language. No hidden behavior.",
    icon: <FunctionSquare size={16} />,
  },
  {
    id: "access",
    title: "Access built in",
    line: "A role is just a list of journeys a person is allowed to take.",
    icon: <ShieldCheck size={16} />,
  },
  {
    id: "running",
    title: "Born running",
    line: "Every app bootstraps with sign-in and a working default app.",
    icon: <Rocket size={16} />,
  },
  {
    id: "json",
    title: "Everything is one JSON language",
    line: "Portable and inspectable — read it, copy it, move it anywhere.",
    icon: <Braces size={16} />,
  },
];

export function ConceptsSection() {
  return (
    <div className="gd-section">
      <div className="gd-section-intro">
        <h2>Concepts</h2>
        <p>
          Everything you build is a small set of pieces that combine into an app. The
          simplest are at the top; each one builds into the bigger thing below it — all
          the way up to the app.
        </p>
      </div>

      <div className="gd-cx">
        {/* LEFT — the building-block stack (simplest at top → app at bottom) */}
        <div>
          <div className="gd-col-head" style={{ borderRadius: "var(--gd-radius)", marginBottom: 14 }}>
            The building blocks
          </div>
          <div className="gd-vflow">
            {BLOCKS.map((b, i) => (
              <div key={b.id}>
                <div className="gd-vnode">
                  <div className="gd-vnode-badge" style={{ position: "relative" }}>
                    {b.icon}
                    <span
                      style={{
                        position: "absolute",
                        top: -6,
                        right: -6,
                        minWidth: 16,
                        height: 16,
                        padding: "0 3px",
                        borderRadius: 8,
                        background: "var(--gd-primary)",
                        color: "#fff",
                        fontSize: 9,
                        fontWeight: 700,
                        lineHeight: "16px",
                        textAlign: "center",
                      }}
                    >
                      L{b.level}
                    </span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <h4>
                      {b.name} <small>· {b.madeOf}</small>
                    </h4>
                    <p>{b.line}</p>
                  </div>
                </div>
                {i < BLOCKS.length - 1 ? <div className="gd-vconn" /> : null}
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — what you can build */}
        <div>
          <div className="gd-col-head" style={{ borderRadius: "var(--gd-radius)", marginBottom: 14 }}>
            What you can build
          </div>
          <div className="gd-power">
            {POWERS.map((p) => (
              <div key={p.id} className="gd-power-card">
                <div className="gd-vnode-badge">{p.icon}</div>
                <div style={{ minWidth: 0 }}>
                  <h4>{p.title}</h4>
                  <p>{p.line}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
